import { createQuery } from 'react-query-kit';
import { type OpeningHours, Weekday, type WeekdayTime } from '../api';
import { PriceLevel } from '../price-level';
import type { GooglePlace } from './GooglePlace';
import { type GooglePlacesApi, type LookupOpts, type PlaceId, TravelMode } from './GooglePlacesApi';

const WEEKDAYS = [
  Weekday.Sunday,
  Weekday.Monday,
  Weekday.Tuesday,
  Weekday.Wednesday,
  Weekday.Thursday,
  Weekday.Friday,
  Weekday.Saturday,
];

const processPoint = ({ day, hour, minute }: google.maps.places.OpeningHoursPoint): WeekdayTime => {
  const time = new Date(0);
  time.setUTCHours(hour, minute);
  const timeString = time.toISOString();
  const tIndex = timeString.indexOf('T');
  const weekday = WEEKDAYS[day];
  if (!weekday) {
    throw new Error(`Unknown weekday: ${day.toString()}`);
  }
  return {
    day: weekday,
    time: timeString.substring(tIndex + 1, tIndex + 9),
  };
};

const processPeriod = (period: google.maps.places.OpeningHoursPeriod): OpeningHours => {
  return {
    open: processPoint(period.open),
    close: period.close ? processPoint(period.close) : null,
  };
};

const processPriceLevel = (
  priceLevel: 'FREE' | 'INEXPENSIVE' | 'MODERATE' | 'EXPENSIVE' | 'VERY_EXPENSIVE' | null,
): PriceLevel | null => {
  switch (priceLevel) {
    case 'FREE':
      return PriceLevel.Free;
    case 'INEXPENSIVE':
      return PriceLevel.Inexpensive;
    case 'MODERATE':
      return PriceLevel.Moderate;
    case 'EXPENSIVE':
      return PriceLevel.Expensive;
    case 'VERY_EXPENSIVE':
      return PriceLevel.VeryExpensive;
    case null:
      return null;
  }
};

export class DefaultGooglePlacesApi implements GooglePlacesApi {
  public readonly lookup = (placeId: PlaceId, opts?: LookupOpts) =>
    createQuery<GooglePlace>({
      queryKey: ['google-place-lookup', placeId, opts],
      fetcher: async () => {
        const emptyPlace = new google.maps.places.Place({
          id: placeId,
          requestedLanguage: opts?.language ?? 'en',
          requestedRegion: opts?.region ?? 'us',
        });
        const { place } = await emptyPlace.fetchFields({
          fields: [
            'addressComponents',
            'displayName',
            'editorialSummary',
            'formattedAddress',
            'googleMapsURI',
            'nationalPhoneNumber',
            'location',
            'priceLevel',
            'primaryTypeDisplayName',
            'rating',
            'regularOpeningHours',
            'reviews',
            'websiteURI',
          ],
        });
        if (!place.addressComponents) {
          throw new Error(`No addressComponents for ${placeId}`);
        }
        if (!place.displayName) {
          throw new Error(`No displayName for ${placeId}`);
        }
        if (place.editorialSummary === undefined) {
          throw new Error(`editorialSummary for ${placeId} not retrieved`);
        }
        if (!place.formattedAddress) {
          throw new Error(`No formattedAddress for ${placeId}`);
        }
        if (!place.googleMapsURI) {
          throw new Error(`No googleMapsURI for ${placeId}`);
        }
        if (place.nationalPhoneNumber === undefined) {
          throw new Error(`nationalPhoneNumber for ${placeId} not retrieved`);
        }
        if (!place.location) {
          throw new Error(`No location for ${placeId}`);
        }
        if (place.priceLevel === undefined) {
          throw new Error(`priceLevel for ${placeId} not retrieved`);
        }
        if (place.primaryTypeDisplayName === undefined) {
          throw new Error(`primaryTypeDisplayName for ${placeId} not retrieved`);
        }
        if (place.rating === undefined) {
          throw new Error(`rating for ${placeId} not retrieved`);
        }
        if (place.regularOpeningHours === undefined) {
          throw new Error(`regularOpeningHours for ${placeId} not retrieved`);
        }
        if (place.reviews === undefined) {
          throw new Error(`reviews for ${placeId} not retrieved`);
        }
        if (place.websiteURI === undefined) {
          throw new Error(`websiteURI for ${placeId} not retrieved`);
        }
        return {
          id: place.id,
          name: place.displayName,
          address: place.formattedAddress,
          neighborhood: place.addressComponents.find(({ types }) => types.includes('neighborhood'))?.longText ?? null,
          phoneNumber: place.nationalPhoneNumber,
          editorialSummary: place.editorialSummary,
          googleMapsUri: place.googleMapsURI,
          location: {
            latitude: place.location.lat(),
            longitude: place.location.lng(),
          },
          openingHours: place.regularOpeningHours?.periods.map(processPeriod) ?? [],
          openingHoursText: place.regularOpeningHours?.weekdayDescriptions ?? [],
          website: place.websiteURI,
          priceLevel: processPriceLevel(place.priceLevel),
          rating: place.rating,
          reviews: place.reviews.map(({ text }) => text).filter((review) => review !== null),
          type: place.primaryTypeDisplayName,
        };
      },
      retry: false,
    });

  public readonly distance = (origin: PlaceId, destination: PlaceId, travelMode: TravelMode) =>
    createQuery({
      queryKey: ['distance', origin, destination, travelMode],
      fetcher: async () => {
        const toGoogle = (mode: TravelMode) => {
          switch (mode) {
            case TravelMode.Driving:
              return google.maps.TravelMode.DRIVING;
            case TravelMode.Walking:
              return google.maps.TravelMode.WALKING;
          }
        };

        // FIXME: What's replacing this?
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        const service = new google.maps.DistanceMatrixService();
        const matrix = await service.getDistanceMatrix({
          origins: [{ placeId: origin }],
          destinations: [{ placeId: destination }],
          travelMode: toGoogle(travelMode),
          unitSystem: google.maps.UnitSystem.METRIC,
        });
        const distance = matrix.rows[0]?.elements[0]?.distance;
        if (!distance) {
          throw new Error(`Distance from ${origin} to ${destination} not found!`);
        }
        return {
          origin,
          destination,
          travelMode,
          distanceMeters: distance.value,
        };
      },
    });
}
